const proxyURL = process.env.REACT_APP_PROXY_URL ||
  "https://saferightfast.app/api/proxy";
const prod = process.env.REACT_APP_RUN_ENV === "prod";
export const ticketCookieName = "TICKET_lighthouseelectric.quickbase.com";

/**
 * Converts a CSS color keyword (e.g., "blue", "red") into its RGB string representation.
 * This works by temporarily creating a DOM element, applying the color,
 * and then reading the computed style.
 *
 * @param {string} colorKeyword - The CSS color name (e.g., "rebeccapurple").
 * @returns {string} The color in "rgb(r, g, b)" format.
 */
export const colorKeywordToRGB = (colorKeyword) => {
  // Create a temporary, non-visible element to measure the color.
  const tempElement = document.createElement("div");
  tempElement.style.color = colorKeyword;
  tempElement.style.display = "none"; // Keep it out of the document flow.
  document.body.appendChild(tempElement);

  // Get the computed style, which will be in RGB format.
  const computedColor = window.getComputedStyle(tempElement).color;

  // Clean up by removing the temporary element.
  document.body.removeChild(tempElement);

  return computedColor;
};

/**
 * Determines whether black or white text should be used on a given RGB background color
 * for the best contrast, based on the YIQ color space formula.
 *
 * @param {string} rgbString - The background color in "rgb(r, g, b)" format.
 * @returns {"#000000" | "#ffffff"} The hex code for black or white text.
 */
export const rgbBackgroundToTextColor = (rgbString) => {
  if (!rgbString) return "#000000";

  // Extract the R, G, and B values from the string.
  const [red, green, blue] = rgbString.replace(/[^\d,]/g, "").split(",");

  // Calculate the perceived brightness (luminance) using the YIQ formula.
  const yiq =
    ((Number(red) * 299) + (Number(green) * 587) + (Number(blue) * 114)) / 1000;

  // Return black for light backgrounds (YIQ >= 186), and white for dark backgrounds.
  return (yiq >= 186) ? "#000000" : "#ffffff";
};

// --- XML & Cookie Utilities ---

/**
 * Parses an XML string into a JavaScript object.
 * Note: This function uses ActiveXObject as a fallback for legacy Internet Explorer versions.
 *
 * @param {string} xmlString - The XML string to parse.
 * @param {string[]} [arrayTags] - An array of tag names that should always be treated as arrays in the output, even if they only appear once.
 * @returns {object} A JavaScript object representing the XML structure.
 * @throws {Error} If the XML string cannot be parsed.
 */
export function parseXml(xmlString, arrayTags = []) {
  let dom = null;
  if (window.DOMParser) {
    dom = new DOMParser().parseFromString(xmlString, "text/xml");
  } else if (window.ActiveXObject) { // Fallback for legacy IE
    dom = new window.ActiveXObject("Microsoft.XMLDOM");
    dom.async = false;
    if (!dom.loadXML(xmlString)) {
      throw new Error(`${dom.parseError.reason} ${dom.parseError.srcText}`);
    }
  } else {
    throw new Error("XML parsing is not supported in this browser.");
  }

  const parseNode = (xmlNode, result) => {
    if (xmlNode.nodeName === "#text") {
      const textContent = xmlNode.nodeValue?.trim();
      if (textContent) {
        result["#text"] = textContent;
      }
      return;
    }

    const jsonNode = {};
    const existingProperty = result[xmlNode.nodeName];

    if (existingProperty) {
      // If the property already exists, convert it to an array.
      if (!Array.isArray(existingProperty)) {
        result[xmlNode.nodeName] = [existingProperty, jsonNode];
      } else {
        result[xmlNode.nodeName].push(jsonNode);
      }
    } else {
      // If it's a new property, create it. If it's in arrayTags, make it an array.
      if (arrayTags.includes(xmlNode.nodeName)) {
        result[xmlNode.nodeName] = [jsonNode];
      } else {
        result[xmlNode.nodeName] = jsonNode;
      }
    }

    if (xmlNode.attributes) {
      for (const attribute of xmlNode.attributes) {
        jsonNode[attribute.nodeName] = attribute.nodeValue;
      }
    }

    for (const childNode of xmlNode.childNodes) {
      parseNode(childNode, jsonNode);
    }
  };

  const result = {};
  for (const node of dom.childNodes) {
    parseNode(node, result);
  }

  return result;
}

/**
 * Retrieves the value of a specific cookie by its name.
 *
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {string | undefined} The cookie's value, or undefined if not found.
 */
export const getCookieValue = (name) => {
  const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
  return match?.pop();
};

// --- API Fetch Wrapper ---

/**
 * A wrapper for the native `fetch` API that handles proxying requests in production.
 * - In development, it makes direct requests
 * - In production, it sends the request details to a dedicated proxy to bypass CORS issues.
 *
 * @param {string | URL} url - The URL to request.
 * @param {RequestInit} options - The options for the fetch request (method, headers, body, etc.).
 * @returns {Promise<Response>} A promise that resolves to the final Response object.
 */
export const pFetch = (url, options, ...rest) => {
  console.log(prod);
  console.log(url, options);
  console.log(getCookieValue(ticketCookieName));
  if (!prod) {
    const newURL = new URL(url);
    return fetch(newURL.pathname + newURL.search, options, ...rest);
  } else {
    const { headers, credentials, body } = options || {};
    const contentType = options?.headers?.["Content-Type"];

    const newOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    };
    const newBody = {};
    const realBody = { url, token: "" };
    // if(!prod) newOptions.mode = "cors";
    if (headers) {
      newBody.headers = {
        ...headers,
        ...(contentType === "application/json"
          ? { referer: "https://lighthouseelectric.quickbase.com" }
          : {}),
      };
    }

    if (credentials === "include") {
      newBody.cookies = {
        [ticketCookieName]: getCookieValue(ticketCookieName),
        "TICKET": getCookieValue(ticketCookieName),
      };
    }
    if (body) newBody.data = body;
    realBody.opts = newBody;
    newOptions.body = JSON.stringify(realBody);
    // let newURL = url.split(".com/")[1];
    console.log(newOptions, realBody);
    return fetch(
      `https://saferightfast.app/api/proxy/${options?.method?.toLowerCase() || "get"
      }`,
      newOptions,
    ).then((res) => Promise.all([res, res.json()])).then(([res, resText]) =>
      new Response(resText, { ...res })
    );
  }
};

export const processAndSortItems = (items) => {
  if (!items || items.length === 0) {
    return [];
  }
  // This function adds category headers based on the 'type' property.
  // We first sort by type to group items together.
  const sortedItems = [...items].sort((a, b) => a.type.localeCompare(b.type));

  const result = [];
  let currentCategory = null;

  sortedItems.forEach((item) => {
    // If the category changes, add a new category header to the result array.
    if (item.type !== currentCategory) {
      result.push({
        type: "category",
        description: item.type,
        // Add a unique key for React rendering if needed, e.g., id: `category-${item.type}`
      });
      currentCategory = item.type;
    }
    // Add the actual item after its category header.
    result.push(item);
  });

  return result;
};
